import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Phone, ArrowUpRight } from 'lucide-react';
import { vendorService } from '../services/api';
import SearchAutocomplete from '../components/SearchAutocomplete';
import Pagination from '../components/Pagination';

const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const Listing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // Filter states
  const [selectedState, setSelectedState] = useState(searchParams.get('state') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('categoryId') || '');
  const [displayedCategory, setDisplayedCategory] = useState(searchParams.get('categoryId') || '');
  const [selectedTags, setSelectedTags] = useState(() => {
    const tagsParam = searchParams.get('tagIds') || searchParams.get('tagId');
    return tagsParam ? tagsParam.split(',').filter(Boolean) : [];
  });
  const [selectedBrands, setSelectedBrands] = useState(() => {
    const brandsParam = searchParams.get('brandIds') || searchParams.get('brandId');
    return brandsParam ? brandsParam.split(',').filter(Boolean) : [];
  });
  const [showBrandsFilter, setShowBrandsFilter] = useState(false);

  // Dropdown list states
  const [states, setStates] = useState([]);
  const [citiesMap, setCitiesMap] = useState({});
  const [categories, setCategories] = useState([]);

  // Listings data states
  const [listings, setListings] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalCombinedCount, setTotalCombinedCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = parseInt(searchParams.get('page'), 10);
    return !isNaN(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
  });
  const [pageSize, setPageSize] = useState(() => {
    const sizeParam = parseInt(searchParams.get('size'), 10);
    return !isNaN(sizeParam) && sizeParam > 0 ? sizeParam : 12;
  });
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  // Load locations and categories on mount
  const loadMetadata = async () => {
    try {
      const locRes = await vendorService.getPublicLocations();
      if (locRes.success && locRes.data) {
        setStates(locRes.data.states || []);
        setCitiesMap(locRes.data.cities || {});
      }

      const catRes = await vendorService.getCategories(false, 'ALL');
      if (catRes.success && catRes.data) {
        setCategories(catRes.data || []);
      }
    } catch (err) {
      console.error("Error loading listing metadata:", err);
    }
  };

  useEffect(() => {
    loadMetadata();
  }, []);

  // Primary data synchronization & fetching effect driven by URL searchParams & categories
  useEffect(() => {
    let isCancelled = false;

    const syncAndFetch = async () => {
      const qParam = searchParams.get('q') || '';
      const stateParam = searchParams.get('state') || '';
      const cityParam = searchParams.get('city') || '';
      const categoryParam = searchParams.get('categoryId') || '';
      const tagParam = searchParams.get('tagId') || '';
      const tagsParam = searchParams.get('tagIds') || '';
      const brandParam = searchParams.get('brandId') || '';
      const brandsParam = searchParams.get('brandIds') || '';
      const searchNameParam = searchParams.get('searchName') || '';
      const pageParam = parseInt(searchParams.get('page'), 10);
      const pageNum = !isNaN(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
      const sizeParam = parseInt(searchParams.get('size'), 10);
      const sizeNum = !isNaN(sizeParam) && sizeParam > 0 ? sizeParam : 12;

      // 1. Sync React input state with URL parameters
      setSelectedState(stateParam);
      setSelectedCity(cityParam);
      setPageSize(sizeNum);
      setCurrentPage(pageNum);

      let resolvedCategoryId = categoryParam;
      let resolvedTags = tagsParam ? tagsParam.split(',').filter(Boolean) : (tagParam ? [tagParam] : []);
      let resolvedBrands = brandsParam ? brandsParam.split(',').filter(Boolean) : (brandParam ? [brandParam] : []);

      // 2. If searchName parameter is present, resolve it to actual categoryId or tagId
      if (searchNameParam && categories.length > 0) {
        const query = searchNameParam.toLowerCase().trim();
        const matchedCategory = categories.find(cat => cat.name.toLowerCase().trim() === query);

        if (matchedCategory) {
          resolvedCategoryId = String(matchedCategory.id);
          resolvedTags = [];
        } else {
          for (const cat of categories) {
            if (cat.tags) {
              const matchedTag = cat.tags.find(tag => tag.name.toLowerCase().trim() === query);
              if (matchedTag) {
                resolvedCategoryId = String(cat.id);
                resolvedTags = [String(matchedTag.id)];
                break;
              }
            }
          }
        }
      }

      // If qParam is present without structured filters, reset category and tags/brands
      if (qParam && !stateParam && !cityParam && !categoryParam && !searchNameParam) {
        resolvedCategoryId = '';
        resolvedTags = [];
        resolvedBrands = [];
      }

      setSelectedCategory(resolvedCategoryId);
      setDisplayedCategory(resolvedCategoryId);
      setSelectedTags(resolvedTags);
      setSelectedBrands(resolvedBrands);

      // 3. Prepare filters and fetch listings
      setLoading(true);
      try {
        const filters = {};
        if (qParam.trim()) filters.q = qParam.trim();
        if (stateParam) filters.state = stateParam;
        if (cityParam) filters.city = cityParam;
        if (resolvedCategoryId && !isNaN(resolvedCategoryId)) filters.categoryId = resolvedCategoryId;
        if (resolvedTags.length > 0) filters.tagIds = resolvedTags.join(',');
        if (resolvedBrands.length > 0) filters.brandIds = resolvedBrands.join(',');

        const res = await vendorService.getPublicListings(filters, pageNum, sizeNum);
        if (!isCancelled) {
          if (res.success && res.data) {
            setListings(res.data.listings || []);
            setTotalItems(res.data.totalItems || 0);
            setTotalCombinedCount(res.data.totalCombinedCount !== undefined ? res.data.totalCombinedCount : (res.data.totalItems || 0));
            setTotalPages(res.data.totalPages || 0);
          } else {
            setListings([]);
            setTotalItems(0);
            setTotalCombinedCount(0);
            setTotalPages(0);
          }
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("Error fetching public listings:", err);
          setListings([]);
          setTotalItems(0);
          setTotalCombinedCount(0);
          setTotalPages(0);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    syncAndFetch();

    return () => {
      isCancelled = true;
    };
  }, [searchParams, categories]);

  // Form submit handler - pushes active filters to URL searchParams
  // Note: Searching from bottom location/category filter bar clears top universal search 'q'
  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const newParams = new URLSearchParams();
    if (selectedState) newParams.set('state', selectedState);
    if (selectedCity) newParams.set('city', selectedCity);
    if (selectedCategory) newParams.set('categoryId', selectedCategory);
    if (pageSize !== 12) newParams.set('size', String(pageSize));
    // Submitting a new search resets to page 1 (no page param)
    setSearchParams(newParams);
  };

  const handleCategoryChange = (val) => {
    setSelectedCategory(val);
    setSelectedTags([]); // Reset tag selection
    setSelectedBrands([]); // Reset brand selection
    setShowBrandsFilter(false); // Reset toggle state
  };

  const handleTagClick = (tagId) => {
    let newTags = [];
    if (tagId) {
      const strId = String(tagId);
      if (selectedTags.includes(strId)) {
        newTags = selectedTags.filter(id => id !== strId);
      } else {
        newTags = [...selectedTags, strId];
      }
    }

    const newParams = new URLSearchParams(searchParams);
    // Remove universal search 'q' and temporary searchName if any
    newParams.delete('q');
    newParams.delete('searchName');
    newParams.delete('tagId');
    newParams.delete('page'); // Reset to page 1 on filter change

    if (selectedState) newParams.set('state', selectedState);
    if (selectedCity) newParams.set('city', selectedCity);
    if (selectedCategory || displayedCategory) {
      newParams.set('categoryId', selectedCategory || displayedCategory);
    }

    if (newTags.length > 0) {
      newParams.set('tagIds', newTags.join(','));
    } else {
      newParams.delete('tagIds');
    }

    setSearchParams(newParams);
  };

  const handleBrandClick = (brandId) => {
    let newBrands = [];
    if (brandId) {
      const strId = String(brandId);
      if (selectedBrands.includes(strId)) {
        newBrands = selectedBrands.filter(id => id !== strId);
      } else {
        newBrands = [...selectedBrands, strId];
      }
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('q');
    newParams.delete('searchName');
    newParams.delete('brandId');
    newParams.delete('page'); // Reset to page 1 on filter change

    if (selectedState) newParams.set('state', selectedState);
    if (selectedCity) newParams.set('city', selectedCity);
    if (selectedCategory || displayedCategory) {
      newParams.set('categoryId', selectedCategory || displayedCategory);
    }

    if (newBrands.length > 0) {
      newParams.set('brandIds', newBrands.join(','));
    } else {
      newParams.delete('brandIds');
    }

    setSearchParams(newParams);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      window.scrollTo({ top: 0, behavior: 'smooth' });

      const newParams = new URLSearchParams(searchParams);
      if (newPage > 0) {
        newParams.set('page', String(newPage + 1));
      } else {
        newParams.delete('page');
      }
      if (pageSize !== 12) {
        newParams.set('size', String(pageSize));
      } else {
        newParams.delete('size');
      }
      setSearchParams(newParams);
    }
  };

  const handlePageSizeChange = (newSize) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');
    if (newSize !== 12) {
      newParams.set('size', String(newSize));
    } else {
      newParams.delete('size');
    }
    setSearchParams(newParams);
  };

  // Get available cities for selected state
  const availableCities = selectedState
    ? (citiesMap[selectedState] || [])
    : Array.from(new Set(Object.values(citiesMap).flat()));

  const activeCategory = categories.find(cat => String(cat.id) === String(displayedCategory));
  const categoryTags = activeCategory && activeCategory.tags
    ? activeCategory.tags.filter(t => t.isActive)
    : [];
  const categoryBrands = activeCategory && activeCategory.brands
    ? activeCategory.brands.filter(b => b.isActive)
    : [];

  return (
    <div className="bg-[#f8f9fa] min-h-screen pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Desktop Search Section (State, City, Category + Search Button) */}
          <form
            onSubmit={handleSearch}
            className="hidden md:flex bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8 flex-row gap-4 items-center justify-between"
          >
            {/* State Select */}
            <div className="w-1/4">
              <SearchAutocomplete
                placeholder="State"
                value={selectedState}
                options={states}
                onSelect={(val) => {
                  setSelectedState(val);
                  setSelectedCity('');
                }}
                inputClassName="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] text-gray-700 cursor-pointer font-medium placeholder-gray-500"
              />
            </div>

            {/* City Select */}
            <div className="w-1/4">
              <SearchAutocomplete
                placeholder="City"
                value={selectedCity}
                options={availableCities}
                onSelect={(val) => {
                  setSelectedCity(val);
                  if (val) {
                    for (const state in citiesMap) {
                      if (citiesMap[state].includes(val)) {
                        setSelectedState(state);
                        break;
                      }
                    }
                  }
                }}
                inputClassName="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] text-gray-700 cursor-pointer font-medium placeholder-gray-500"
              />
            </div>

            {/* Category Select */}
            <div className="w-1/4">
              <SearchAutocomplete
                placeholder="Category"
                value={selectedCategory}
                options={categories}
                onSelect={(val) => handleCategoryChange(val)}
                inputClassName="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] text-gray-700 cursor-pointer font-medium placeholder-gray-500"
              />
            </div>

            {/* Search Action Button */}
            <div className="flex gap-2 w-auto flex-shrink-0">
              <button
                type="submit"
                className="bg-[#2ab391] hover:bg-[#23977a] text-white p-3 rounded-xl flex items-center justify-center gap-2 font-medium px-8 transition-colors shadow-sm cursor-pointer"
                title="Search"
              >
                <Search size={18} />
              </button>
            </div>
          </form>

          {/* Mobile Search Section (State, City, Category + Search Button) */}
          <form
            onSubmit={handleSearch}
            className="block md:hidden bg-white rounded-xl shadow-md p-1 mb-8 w-full border border-gray-150"
          >
            <div className="flex items-center w-full gap-1">
              {/* State Select */}
              <div className="flex-1 min-w-0 relative flex items-center border-r border-gray-150">
                <SearchAutocomplete
                  placeholder="State"
                  value={selectedState}
                  options={states}
                  onSelect={(val) => {
                    setSelectedState(val);
                    setSelectedCity('');
                  }}
                  dropdownClassName="left-0 right-auto min-w-[190px] sm:min-w-full shadow-xl rounded-xl border border-gray-150"
                  inputClassName="w-full bg-transparent pl-2 pr-6 py-2 text-[11px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
                />
              </div>

              {/* City Select */}
              <div className="flex-1 min-w-0 relative flex items-center border-r border-gray-150">
                <SearchAutocomplete
                  placeholder="City"
                  value={selectedCity}
                  options={availableCities}
                  onSelect={(val) => {
                    setSelectedCity(val);
                    if (val) {
                      for (const state in citiesMap) {
                        if (citiesMap[state].includes(val)) {
                          setSelectedState(state);
                          break;
                        }
                      }
                    }
                  }}
                  dropdownClassName="left-0 right-auto min-w-[190px] sm:min-w-full shadow-xl rounded-xl border border-gray-150"
                  inputClassName="w-full bg-transparent pl-2 pr-6 py-2 text-[11px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
                />
              </div>

              {/* Category Select */}
              <div className="flex-[1.2] min-w-0 relative flex items-center pr-1">
                <SearchAutocomplete
                  placeholder="Category"
                  value={selectedCategory}
                  options={categories}
                  onSelect={(val) => handleCategoryChange(val)}
                  dropdownClassName="right-0 left-auto min-w-[210px] sm:min-w-full shadow-xl rounded-xl border border-gray-150"
                  inputClassName="w-full bg-transparent pl-2 pr-6 py-2 text-[11px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
                />
              </div>

              {/* Search Button */}
              <button
                type="submit"
                className="bg-primary text-white rounded-lg hover:bg-opacity-90 transition-all flex items-center justify-center w-9 h-9 flex-shrink-0 cursor-pointer"
                title="Search"
              >
                <Search size={14} strokeWidth={2.5} />
              </button>
            </div>
          </form>



        {/* Full-width Category Tags or Brands Filter Panel */}
        {displayedCategory && (categoryTags.length > 0 || categoryBrands.length > 0) && (
          <div className="w-full mb-3 md:mb-8 flex flex-col gap-2.5 md:gap-4 animate-in fade-in duration-200 text-left">
            {/* Tab Headers */}
            <div className="flex border-b border-gray-200 gap-4 md:gap-6">
              {categoryTags.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowBrandsFilter(false)}
                  className={`pb-1.5 md:pb-2 text-xs md:text-sm font-semibold border-b-2 transition-all cursor-pointer ${
                    !showBrandsFilter
                      ? 'border-[#2ab391] text-[#2ab391]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Tags
                </button>
              )}
              {categoryBrands.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowBrandsFilter(true)}
                  className={`pb-1.5 md:pb-2 text-xs md:text-sm font-semibold border-b-2 transition-all cursor-pointer ${
                    showBrandsFilter
                      ? 'border-[#2ab391] text-[#2ab391]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Brands
                </button>
              )}
            </div>

            {/* Render Brands list if active */}
            {showBrandsFilter ? (
              categoryBrands.length > 0 && (
                <div
                  className="flex flex-wrap gap-1.5 md:gap-2.5 justify-start overflow-y-auto max-h-[100px] md:max-h-[82px] pr-1 md:pr-2 items-center text-left py-0.5"
                  style={{ scrollbarWidth: 'thin' }}
                >
                  <button
                    onClick={() => handleBrandClick('')}
                    className={`px-3 py-1 md:px-4 md:py-1.5 rounded-full text-[11px] md:text-xs font-normal transition-all duration-200 border cursor-pointer bg-transparent whitespace-nowrap ${
                      selectedBrands.length === 0
                        ? 'border-[#2ab391] text-[#2ab391] font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    All Brands
                  </button>
                  {categoryBrands.map(brand => {
                    const isSelected = selectedBrands.includes(String(brand.id));
                    return (
                      <button
                        key={brand.id}
                        onClick={() => handleBrandClick(brand.id)}
                        className={`px-3 py-1 md:px-4 md:py-1.5 rounded-full text-[11px] md:text-xs font-normal transition-all duration-200 border cursor-pointer bg-transparent whitespace-nowrap ${
                          isSelected
                            ? 'border-[#2ab391] text-[#2ab391] font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                        }`}
                      >
                        {brand.name}
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              /* Render Tags list if active */
              categoryTags.length > 0 && (
                <div
                  className="flex flex-wrap gap-1.5 md:gap-2.5 justify-start overflow-y-auto max-h-[100px] md:max-h-[82px] pr-1 md:pr-2 items-center text-left py-0.5"
                  style={{ scrollbarWidth: 'thin' }}
                >
                  <button
                    onClick={() => handleTagClick('')}
                    className={`px-3 py-1 md:px-4 md:py-1.5 rounded-full text-[11px] md:text-xs font-normal transition-all duration-200 border cursor-pointer bg-transparent whitespace-nowrap ${
                      selectedTags.length === 0
                        ? 'border-[#2ab391] text-[#2ab391] font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    All Tags
                  </button>
                  {categoryTags.map(tag => {
                    const isSelected = selectedTags.includes(String(tag.id));
                    return (
                      <button
                        key={tag.id}
                        onClick={() => handleTagClick(tag.id)}
                        className={`px-3 py-1 md:px-4 md:py-1.5 rounded-full text-[11px] md:text-xs font-normal transition-all duration-200 border cursor-pointer bg-transparent whitespace-nowrap ${
                          isSelected
                            ? 'border-[#2ab391] text-[#2ab391] font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                        }`}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}

        {/* Directory Controls */}
        <div className="flex justify-between items-center mb-3 md:mb-6">
          <div className="text-sm font-semibold text-gray-700">
            {totalCombinedCount} Results
          </div>
        </div>

        {/* Listings Display */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2ab391]"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-[#eaf7f4] text-[#2ab391] rounded-full flex items-center justify-center mx-auto mb-4">
              <Search size={28} />
            </div>
            <p className="text-gray-500 text-sm max-w-sm mx-auto">
              Our vendors are on the way to serve you
              <br />
              Please visit us after 7 days & we are ready to serve you.
            </p>
          </div>
        ) : (
          <>
            {/* Separate Individual listings vs Company listings for clean grid system */}
            {(() => {
              const individualListings = listings.filter(l => l.listingType === 'INDIVIDUAL' || !l.profileBannerUrl);
              const companyListings = listings.filter(l => l.listingType !== 'INDIVIDUAL' && Boolean(l.profileBannerUrl));

              return (
                <div className="space-y-6">
                  {/* Company Listings (Standard Top Banner Layout - 3 per row on Desktop, full-width on Mobile) */}
                  {companyListings.length > 0 && (
                    <div>
                      {/* Desktop Grid Layout */}
                      <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6">
                        {companyListings.map(listing => {
                          const displayTags = listing.tags || [];
                          const showTags = displayTags.slice(0, 3);
                          const extraCount = displayTags.length - showTags.length;

                          return (
                            <div
                              key={`desk-comp-${listing.id}`}
                              onClick={() => navigate(`/listing/${slugify(listing.companyName)}`)}
                              className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col hover:shadow-md transition-shadow cursor-pointer"
                            >
                              {/* Banner Image Area */}
                              <div className="h-48 w-full bg-white relative hover:opacity-95 transition-opacity">
                                <img
                                  src={getImageUrl(listing.profileBannerUrl)}
                                  alt={listing.companyName}
                                  className="w-full h-full object-contain bg-white"
                                />
                                {listing.membershipPlan === 'PREMIUM' && listing.membershipStatus === 'ACTIVE' && (
                                  <span className="absolute top-4 right-4 bg-[#2ab391] text-white text-[11px] font-bold px-3 py-1 rounded-full shadow-sm">
                                    Trusted
                                  </span>
                                )}
                              </div>

                              {/* Card Main Body */}
                              <div className="p-5 flex-1 flex flex-col justify-between">
                                <div>
                                  <h4 className="font-bold text-gray-800 text-lg mb-1 leading-snug line-clamp-1">
                                    {listing.companyName}
                                  </h4>

                                  <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-3">
                                    <MapPin size={13} className="text-[#2ab391]" />
                                    <span>{listing.area ? `${listing.area}, ` : ''}{listing.city ? `${listing.city}, ` : ''}{listing.state}</span>
                                  </div>

                                  <div className="flex flex-wrap gap-1.5 mb-4">
                                    {showTags.map(tag => (
                                      <span key={tag.id} className="bg-gray-50 text-gray-600 text-[11px] font-medium px-2.5 py-1 rounded-md border border-gray-100">
                                        {tag.name}
                                      </span>
                                    ))}
                                    {extraCount > 0 && (
                                      <span className="bg-[#e0f7f8] text-[#2ab391] text-[11px] font-medium px-2.5 py-1 rounded-md">
                                        +{extraCount} more
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Footer Contact Row */}
                                <div className="border-t border-gray-100 pt-4 flex items-center justify-between gap-2 mt-2">
                                  {((listing.membershipPlan === 'PROFESSIONAL' || listing.membershipPlan === 'PREMIUM') && listing.membershipStatus === 'ACTIVE') ? (
                                    <div className="flex gap-2">
                                      {listing.phone ? (
                                        <a
                                          href={`tel:${listing.phone}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="flex items-center gap-1.5 bg-[#12B76A] hover:bg-[#0fa15c] text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors shadow-sm"
                                        >
                                          <Phone size={12} />
                                          <span>{listing.phone}</span>
                                        </a>
                                      ) : (
                                        <div className="text-gray-400 text-xs">No Phone</div>
                                      )}

                                      {listing.phone && (
                                        <a
                                          href={`https://wa.me/91${listing.phone.replace(/[^0-9]/g, '')}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold px-3 py-2 rounded-lg transition-all"
                                        >
                                          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" className="text-[#25D366]">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                          </svg>
                                          <span>WhatsApp</span>
                                        </a>
                                      )}
                                    </div>
                                  ) : null}

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/listing/${slugify(listing.companyName)}`);
                                    }}
                                    className="bg-gray-900 hover:bg-gray-800 text-white p-2 rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                                    title="View Details"
                                  >
                                    <ArrowUpRight size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Mobile List View Layout */}
                      <div className="md:hidden flex flex-col gap-3">
                        {companyListings.map(listing => {
                          const displayTags = listing.tags || [];
                          const showTags = displayTags.slice(0, 2);
                          const extraCount = displayTags.length - showTags.length;

                          return (
                            <div
                              key={`mob-comp-${listing.id}`}
                              onClick={() => navigate(`/listing/${slugify(listing.companyName)}`)}
                              className="bg-white rounded-2xl overflow-hidden shadow-xs border border-gray-100 p-3 flex gap-3 hover:shadow-md transition-all cursor-pointer"
                            >
                              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-white border border-gray-100 flex-shrink-0 relative flex items-center justify-center">
                                <img
                                  src={getImageUrl(listing.profileBannerUrl)}
                                  alt={listing.companyName}
                                  className="w-full h-full object-contain bg-white"
                                />
                                {listing.membershipPlan === 'PREMIUM' && listing.membershipStatus === 'ACTIVE' && (
                                  <span className="absolute top-0 left-0 z-10 bg-[#2ab391] text-white text-[9px] font-bold px-2 py-0.5 rounded-tl-xl rounded-br-md shadow-xs leading-none">
                                    Trusted
                                  </span>
                                )}
                              </div>

                              <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                                <div>
                                  <h4 className="font-bold text-gray-900 text-sm leading-snug truncate mb-0.5">
                                    {listing.companyName}
                                  </h4>

                                  <div className="flex items-center gap-1 text-gray-500 text-[11px] truncate mb-1.5">
                                    <MapPin size={11} className="text-[#2ab391] flex-shrink-0" />
                                    <span className="truncate">{listing.area ? `${listing.area}, ` : ''}{listing.city ? `${listing.city}, ` : ''}{listing.state}</span>
                                  </div>

                                  {showTags.length > 0 && (
                                    <div className="flex items-center flex-nowrap gap-1 mb-1.5 overflow-hidden max-w-full">
                                      {showTags.map(tag => (
                                        <span key={tag.id} className="bg-gray-50 text-gray-600 text-[10px] font-medium px-2 py-0.5 rounded-md border border-gray-100 whitespace-nowrap truncate max-w-[90px]">
                                          {tag.name}
                                        </span>
                                      ))}
                                      {extraCount > 0 && (
                                        <span className="bg-[#e0f7f8] text-[#2ab391] text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap shrink-0">
                                          +{extraCount} more
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="border-t border-gray-100 pt-1.5 flex items-center justify-between gap-1.5 mt-1">
                                  {((listing.membershipPlan === 'PROFESSIONAL' || listing.membershipPlan === 'PREMIUM') && listing.membershipStatus === 'ACTIVE') ? (
                                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                      {listing.phone ? (
                                        <>
                                          <a
                                            href={`tel:${listing.phone}`}
                                            onClick={(e) => e.stopPropagation()}
                                            className="flex-1 min-w-0 flex items-center justify-center gap-1 bg-[#12B76A] hover:bg-[#0fa15c] text-white text-[11px] font-semibold py-1.5 px-2 rounded-lg transition-colors truncate shadow-xs"
                                          >
                                            <Phone size={11} className="flex-shrink-0" />
                                            <span className="truncate">{listing.phone}</span>
                                          </a>

                                          <a
                                            href={`https://wa.me/91${listing.phone.replace(/[^0-9]/g, '')}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="flex items-center justify-center gap-1 border border-gray-200 hover:bg-gray-50 text-gray-700 text-[11px] font-semibold py-1.5 px-2 rounded-lg transition-all shadow-xs"
                                          >
                                            <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" className="text-[#25D366]">
                                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                            </svg>
                                            <span>WhatsApp</span>
                                          </a>
                                        </>
                                      ) : null}
                                    </div>
                                  ) : null}

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/listing/${slugify(listing.companyName)}`);
                                    }}
                                    className="bg-gray-900 hover:bg-gray-800 text-white p-1.5 rounded-lg transition-colors flex items-center justify-center shrink-0 cursor-pointer shadow-xs"
                                    title="View Details"
                                  >
                                    <ArrowUpRight size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Individual Business Listings Grid (4 per row on Desktop, 2 per row on Mobile) */}
                  {individualListings.length > 0 && (
                    <div>
                      {/* Desktop Grid (4 items per row) */}
                      <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {individualListings.map(listing => (
                          <div
                            key={`desk-ind-${listing.id}`}
                            onClick={() => navigate(`/listing/${slugify(listing.companyName)}`)}
                            className="bg-white rounded-2xl overflow-hidden shadow-xs border border-gray-100 p-4 flex flex-col justify-between hover:shadow-md transition-all cursor-pointer select-none"
                          >
                            <div>
                              <h4 className="font-bold text-gray-900 text-base leading-snug truncate mb-1">
                                {listing.companyName}
                              </h4>

                              <div className="flex items-center gap-1.5 text-gray-500 text-xs truncate mb-3">
                                <MapPin size={13} className="text-[#2ab391] flex-shrink-0" />
                                <span className="truncate">
                                  {listing.area ? `${listing.area}, ` : ''}
                                  {listing.city ? `${listing.city}, ` : ''}
                                  {listing.state}
                                </span>
                              </div>
                            </div>

                            {/* Contact Action Row */}
                            <div className="border-t border-gray-100 pt-3 flex items-center justify-between gap-1.5 mt-2">
                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                {listing.phone ? (
                                  <>
                                    <a
                                      href={`tel:${listing.phone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex-1 min-w-0 flex items-center justify-center gap-1 bg-[#12B76A] hover:bg-[#0fa15c] text-white text-xs font-semibold py-1.5 px-2 rounded-lg transition-colors truncate shadow-xs"
                                    >
                                      <Phone size={11} className="flex-shrink-0" />
                                      <span className="truncate">{listing.phone}</span>
                                    </a>

                                    <a
                                      href={`https://wa.me/91${listing.phone.replace(/[^0-9]/g, '')}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex items-center justify-center gap-1 border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold py-1.5 px-2 rounded-lg transition-all shadow-xs shrink-0"
                                    >
                                      <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" className="text-[#25D366]">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                      </svg>
                                      <span className="hidden xl:inline">WhatsApp</span>
                                    </a>
                                  </>
                                ) : null}
                              </div>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/listing/${slugify(listing.companyName)}`);
                                }}
                                className="bg-gray-900 hover:bg-gray-800 text-white p-1.5 rounded-lg transition-colors flex items-center justify-center shrink-0 cursor-pointer shadow-xs"
                                title="View Details"
                              >
                                <ArrowUpRight size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Mobile Grid (2 items per row) */}
                      <div className="md:hidden grid grid-cols-2 gap-3">
                        {individualListings.map(listing => (
                          <div
                            key={`mob-ind-${listing.id}`}
                            onClick={() => navigate(`/listing/${slugify(listing.companyName)}`)}
                            className="bg-white rounded-2xl overflow-hidden shadow-xs border border-gray-100 p-3 flex flex-col justify-between hover:shadow-md transition-all cursor-pointer select-none"
                          >
                            <div>
                              <h4 className="font-bold text-gray-900 text-xs sm:text-sm leading-snug truncate mb-0.5">
                                {listing.companyName}
                              </h4>

                              <div className="flex items-center gap-1 text-gray-500 text-[11px] truncate mb-2">
                                <MapPin size={11} className="text-[#2ab391] flex-shrink-0" />
                                <span className="truncate">
                                  {listing.area ? `${listing.area}, ` : ''}
                                  {listing.city ? `${listing.city}, ` : ''}
                                  {listing.state}
                                </span>
                              </div>
                            </div>

                            {/* Contact Action Bar */}
                            <div className="border-t border-gray-100 pt-2 flex items-center justify-between gap-1 mt-1">
                              <div className="flex items-center gap-1 flex-1 min-w-0">
                                {listing.phone ? (
                                  <>
                                    <a
                                      href={`tel:${listing.phone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex-1 min-w-0 flex items-center justify-center gap-1 bg-[#12B76A] hover:bg-[#0fa15c] text-white text-[10px] sm:text-[11px] font-semibold py-1.5 px-1 rounded-lg transition-colors truncate shadow-xs"
                                    >
                                      <Phone size={10} className="flex-shrink-0" />
                                      <span className="truncate">{listing.phone}</span>
                                    </a>

                                    <a
                                      href={`https://wa.me/91${listing.phone.replace(/[^0-9]/g, '')}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex items-center justify-center gap-1 border border-gray-200 hover:bg-gray-50 text-gray-700 text-[10px] sm:text-[11px] font-semibold py-1.5 px-1.5 rounded-lg transition-all shadow-xs shrink-0"
                                    >
                                      <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor" className="text-[#25D366]">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                      </svg>
                                      <span className="hidden sm:inline">WhatsApp</span>
                                    </a>
                                  </>
                                ) : null}
                              </div>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/listing/${slugify(listing.companyName)}`);
                                }}
                                className="bg-gray-900 hover:bg-gray-800 text-white p-1 rounded-lg transition-colors flex items-center justify-center shrink-0 cursor-pointer shadow-xs"
                                title="View Details"
                              >
                                <ArrowUpRight size={13} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}

        {/* Pagination controls */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[12, 24, 36]}
          className="mt-8"
        />

      </div>
    </div>
  );
};

export default Listing;
